import React, { useState } from 'react';
import MarketingLayout from '../components/MarketingLayout';
import {
  Box, Container, Typography, Button, Grid, Card, CardContent,
  TextField, IconButton, Paper,
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const TEAL = '#0d9488';
const DARK_BG = '#052d50';
const GRADIENT = 'linear-gradient(135deg, #052d50 0%, #478289 100%)';

const stats = [
  { value: '$550M', label: 'AUM' },
  { value: '110', label: 'Portfolio Companies' },
  { value: '20,000', label: 'Registered Members' },
  { value: '27', label: 'Profitable Exits' },
];

const portfolioCompanies = [
  'Company A', 'Company B', 'Company C', 'Company D',
  'Company E', 'Company F', 'Company G', 'Company H',
];

const pressLogos = ['TechCrunch', 'Fortune', 'Bloomberg', 'Forbes'];

const Homepage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');

  return (
    <MarketingLayout>
    <Box>
      <Helmet>
        <title>iAngels — Venture Beyond | Investing in Israeli Innovation</title>
        <meta name="description" content="iAngels is your gateway to investing in Israeli innovation. Access top Israeli tech startups and venture capital opportunities." />
      </Helmet>

      {/* Hero Section */}
      <Box sx={{ background: GRADIENT, color: 'white', py: { xs: 8, md: 14 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h2" component="h1" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Venture Beyond
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, fontWeight: 300 }}>
            We are iAngels, Your Gateway to Investing in Israeli Innovation
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/login')}
            sx={{ bgcolor: TEAL, px: 5, py: 1.5, fontSize: '1.1rem', '&:hover': { bgcolor: '#0b7d73' } }}
          >
            Join Us
          </Button>
        </Container>
      </Box>

      {/* Stats Bar */}
      <Box sx={{ bgcolor: DARK_BG, py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} justifyContent="center">
            {stats.map((s) => (
              <Grid item xs={6} md={3} key={s.label} sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ color: TEAL, fontWeight: 700 }}>{s.value}</Typography>
                <Typography variant="body1" sx={{ color: 'white', opacity: 0.8, mt: 0.5 }}>{s.label}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Video Embed Placeholder */}
      <Box sx={{ py: 8, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="md">
          <Box
            sx={{
              position: 'relative',
              bgcolor: '#1a1a2e',
              borderRadius: 3,
              aspectRatio: '16/9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <IconButton sx={{ bgcolor: TEAL, color: 'white', width: 72, height: 72, '&:hover': { bgcolor: '#0b7d73' } }}>
              <PlayArrowIcon sx={{ fontSize: 40 }} />
            </IconButton>
          </Box>
        </Container>
      </Box>

      {/* Portfolio Highlights */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 5, color: DARK_BG }}>
            Portfolio Highlights
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {portfolioCompanies.map((name) => (
              <Grid item xs={6} sm={3} md={1.5} key={name}>
                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: '#f8f9fa', borderRadius: 2 }}>
                  <Box sx={{ width: 64, height: 64, bgcolor: '#e0e0e0', borderRadius: '50%', mx: 'auto', mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">{name}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Investors & Entrepreneurs Cards */}
      <Box sx={{ py: 8, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: DARK_BG }}>Investors</Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Access exclusive investment opportunities in Israel's most promising startups. Build a diversified portfolio with iAngels.
                  </Typography>
                  <Button variant="contained" onClick={() => navigate('/login')} sx={{ bgcolor: TEAL, px: 4, '&:hover': { bgcolor: '#0b7d73' } }}>
                    Invest with us
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: DARK_BG }}>Israeli Entrepreneurs</Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Looking for smart capital and strategic support? Partner with iAngels to fuel your startup's growth.
                  </Typography>
                  <Button variant="contained" onClick={() => navigate('/login')} sx={{ bgcolor: TEAL, px: 4, '&:hover': { bgcolor: '#0b7d73' } }}>
                    Apply here
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Choose Your Path */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: DARK_BG }}>
            Choose Your Path with iAngels
          </Typography>
          <Typography variant="h6" sx={{ color: TEAL, mb: 2, fontWeight: 600 }}>iAngels Capital</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            iAngels Capital is our flagship fund, offering investors diversified exposure to Israel's top tech companies.
            Our experienced team sources, evaluates, and manages investments — so you can benefit from Israeli innovation
            with confidence and ease.
          </Typography>
        </Container>
      </Box>

      {/* We Are iAngels */}
      <Box sx={{ py: 8, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: '#e0e0e0', borderRadius: 3, aspectRatio: '4/3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">Team Photo</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: DARK_BG }}>We are iAngels</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                iAngels was founded with a mission to democratize access to venture capital in Israel. Our team brings
                decades of experience in technology, finance, and entrepreneurship. We combine deep local knowledge with
                global networks to identify and support the next generation of Israeli innovation.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/about')} sx={{ borderColor: TEAL, color: TEAL }}>
                Learn More
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* As Seen On */}
      <Box sx={{ py: 6 }}>
        <Container maxWidth="md">
          <Typography variant="overline" align="center" display="block" sx={{ mb: 3, letterSpacing: 3, color: 'text.secondary' }}>
            As Seen On
          </Typography>
          <Grid container spacing={4} justifyContent="center" alignItems="center">
            {pressLogos.map((name) => (
              <Grid item xs={6} sm={3} key={name} sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#bbb', fontWeight: 700, fontStyle: 'italic' }}>{name}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonial */}
      <Box sx={{ py: 8, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontStyle: 'italic', mb: 3, color: DARK_BG, lineHeight: 1.6 }}>
            "iAngels has given me access to investment opportunities that I simply couldn't find anywhere else.
            The team's expertise and transparency make all the difference."
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: TEAL }}>Rolf Garde</Typography>
          <Typography variant="body2" color="text.secondary">Investor</Typography>
        </Container>
      </Box>

      {/* Start Investing CTA */}
      <Box sx={{ py: 8, background: GRADIENT, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
            Start Investing With Us
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 4 }}>
            Join thousands of investors gaining access to Israel's top tech companies.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/login')}
            sx={{ bgcolor: 'white', color: DARK_BG, px: 5, py: 1.5, fontWeight: 700, '&:hover': { bgcolor: '#e0e0e0' } }}
          >
            Get Started
          </Button>
        </Container>
      </Box>

      {/* Newsletter Signup */}
      <Box sx={{ py: 6 }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: DARK_BG }}>Stay in the Loop</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Subscribe to our newsletter for the latest on Israeli tech and investment opportunities.
          </Typography>
          <Box component="form" onSubmit={(e) => e.preventDefault()} sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth size="small" placeholder="Your email address"
              value={email} onChange={(e) => setEmail(e.target.value)}
            />
            <Button variant="contained" type="submit" sx={{ bgcolor: TEAL, px: 3, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#0b7d73' } }}>
              Subscribe
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: DARK_BG, color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>iAngels</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationOnIcon sx={{ fontSize: 18, mr: 1, opacity: 0.7 }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Rothschild Blvd 18, Tel Aviv</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon sx={{ fontSize: 18, mr: 1, opacity: 0.7 }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>info@iangels.com</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon sx={{ fontSize: 18, mr: 1, opacity: 0.7 }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>+972-3-XXX-XXXX</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Company</Typography>
              {['About', 'Team', 'Careers', 'Contact'].map((l) => (
                <Typography key={l} variant="body2" sx={{ opacity: 0.7, mb: 0.5, cursor: 'pointer', '&:hover': { opacity: 1 } }}>{l}</Typography>
              ))}
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Invest</Typography>
              {['How It Works', 'Portfolio', 'FAQ', 'Glossary'].map((l) => (
                <Typography key={l} variant="body2" sx={{ opacity: 0.7, mb: 0.5, cursor: 'pointer', '&:hover': { opacity: 1 } }}>{l}</Typography>
              ))}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Follow Us</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton size="small" sx={{ color: 'white' }}><LinkedInIcon /></IconButton>
                <IconButton size="small" sx={{ color: 'white' }}><TwitterIcon /></IconButton>
                <IconButton size="small" sx={{ color: 'white' }}><FacebookIcon /></IconButton>
              </Box>
            </Grid>
          </Grid>
          <Typography variant="body2" sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255,255,255,0.1)', opacity: 0.5, textAlign: 'center' }}>
            © {new Date().getFullYear()} iAngels. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
    </MarketingLayout>
  );
};

export default Homepage;
